import {
  Badge,
  badgeStateToColor,
  BodySmall,
  ChevronRightIcon,
  evaluateKpi,
  getScoreColor,
  KpiScoreCard,
  KPI_METRIC_TYPE,
  theme,
} from '@arnold/common';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import type { NewReport } from '../hooks/useNewReport';
import { metricDisplay, type CustomMetricSummary, type MetricPart } from '@arnold/core';
import { formatMetricNumber } from '../definition/formatMetricNumber';
import { localizedText, type ReportMetricDefinition } from '../definition/types';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';

/**
 * A metric together with the values of the parts it is made of. Parts are whatever the calculation composes:
 * questions of an average, or metrics of a composite. Clicking a part opens its own detail.
 */
export function MetricBreakdownCard({
  report,
  metric,
  summary,
  baseUrl,
  className,
}: {
  report: NewReport;
  metric: ReportMetricDefinition;
  summary: CustomMetricSummary;
  /** Report root url the question and metric detail routes are resolved against. */
  baseUrl: string;
  className?: string;
}) {
  const { t, i18n } = useTranslation('newReport');
  const { t: tKpiCard } = useTranslation('kpiCard');
  const history = useHistory();
  const { search } = useLocation();
  const metrics = useDefinitionMetrics();
  const display = metricDisplay(summary, metric.presentation.format);
  const partLabel = (part: MetricPart): string => {
    if (part.kind === 'metric') {
      const child = metrics.find((item) => item.id === part.metricId);
      return localizedText(child?.title, i18n.language) || t('definition.unavailable');
    }
    const question = report.rawData?.questions.find((item) => item.index === part.questionIndex);
    return (
      (part.statementIndex !== undefined ? question?.statementsLocal?.[part.statementIndex] : undefined) ??
      question?.text ??
      t('definition.unavailable')
    );
  };
  const openPart = (part: MetricPart) =>
    history.push({
      pathname:
        part.kind === 'metric' ? `${baseUrl}/metric/${part.metricId}` : `${baseUrl}/detail/${part.questionIndex}`,
      search,
    });
  const evaluation = (value: number | undefined, scale: boolean | undefined) =>
    value !== undefined && scale ? evaluateKpi(value, KPI_METRIC_TYPE.SCALE, tKpiCard) : undefined;
  const format = (value: number, scale: boolean | undefined) =>
    formatMetricNumber(value, i18n.language, scale ? 'scale' : undefined);
  const own = evaluation(display.value, summary.scale);
  // Best first; parts without a value go last.
  const parts = [...(summary.parts ?? [])].sort(
    (a, b) => (b.value ?? Number.NEGATIVE_INFINITY) - (a.value ?? Number.NEGATIVE_INFINITY),
  );
  return (
    <Card
      className={className}
      title={localizedText(metric.title, i18n.language) || t('definition.unavailable')}
      subtext={localizedText(metric.description, i18n.language)}
      value={display.value === undefined ? '—' : format(display.value, summary.scale)}
      valueSuffix={display.value === undefined ? undefined : display.suffix}
      state={own?.state}
      badgeText={own?.badgeText}
      detailRows={
        parts.length > 0 ? (
          <PartsGrid>
            {parts.map((part) => {
              const partEvaluation = evaluation(part.value, part.scale);
              return (
                <PartButton key={part.id} type="button" onClick={() => openPart(part)}>
                  <PartLabel>{partLabel(part)}</PartLabel>
                  <PartValue>
                    {partEvaluation && (
                      <Badge
                        text={partEvaluation.badgeText}
                        color={badgeStateToColor(partEvaluation.state)}
                        subtle
                        size="Small"
                      />
                    )}
                    <BodySmall bold color={getScoreColor(partEvaluation?.state ?? 'normal')}>
                      {part.value === undefined ? '—' : format(part.value, part.scale)}
                    </BodySmall>
                    <ChevronRightIcon />
                  </PartValue>
                </PartButton>
              );
            })}
          </PartsGrid>
        ) : undefined
      }
    />
  );
}

/* The common card carries outer margins the overview grid gap already provides. */
const Card = styled(KpiScoreCard)`
  height: 100%;
  margin: 0;
`;

const PartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: ${theme.spacing.g};

  @media (max-width: ${theme.breakpoint.small}) {
    grid-template-columns: 1fr;
  }
`;

const PartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.d};
  width: 100%;
  padding: ${theme.spacing.e} 0;
  border: none;
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  background: none;
  text-align: left;
  break-inside: avoid;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${theme.colors.actionPrimary.focused};
    outline-offset: 2px;
  }
`;

const PartLabel = styled.span`
  min-width: 0;
  font-family: 'Roboto', sans-serif;
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.text.primary};
`;

const PartValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.c};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
  svg path {
    fill: ${theme.colors.text.secondary};
  }
`;
